import { useEffect, useState } from 'react';
import * as d3 from 'd3';
import type { ScatterClassIconType, ScatterStudentIconType } from '../../components/charts/types';
import type { IconsType } from '../../context/IconContext';
import * as StudentIcon from '../../components/charts/icons/students';
import * as ClassIcon from '../../components/charts/icons/classes';
import ChevronUp from '../../components/charts/icons/chevron-up.svg';
import ChevronDown from '../../components/charts/icons/chevron-down.svg';
import ChartColoured from '../../components/charts/icons/TwoPeople.svg';
import BadSmiley from '../../components/charts/icons/smileys/BadSmiley.svg';
import GoodSmiley from '../../components/charts/icons/smileys/GoodSmiley.svg';
import ExcellentSmiley from '../../components/charts/icons/smileys/ExcellentSmiley.svg';
import CollapseOn from '../../components/charts/icons/collapse-on.svg';
import CollapseOff from '../../components/charts/icons/collapse-off.svg';

const prepareXml = (xml: XMLDocument): Node => {
  return xml.importNode(xml.documentElement, true);
};

const getSvg = async(svgUrl: string): Promise<Node> => prepareXml(await d3.xml(svgUrl));

const getStudentIcons = async(): Promise<ScatterStudentIconType> => ({
  primary: {
    close: await getSvg(StudentIcon.SelectedClose),
    open: await getSvg(StudentIcon.SelectedOpen),
    bounce: await getSvg(StudentIcon.SelectedHeadBounce),
    jump: await getSvg(StudentIcon.SelectedJumpSpin),
    snooze: await getSvg(StudentIcon.SelectedSnooze),
    skipping_rope: await getSvg(StudentIcon.SelectedSkippingRope),
    glasses: await getSvg(StudentIcon.SelectedGlasses),
    juggler: await getSvg(StudentIcon.SelectedJuggler),
  },
  male: {
    close: await getSvg(StudentIcon.MaleClose),
    open: await getSvg(StudentIcon.MaleOpen),
    bounce: await getSvg(StudentIcon.MaleHeadBounce),
    jump: await getSvg(StudentIcon.MaleJumpSpin),
    snooze: await getSvg(StudentIcon.MaleSnooze),
    skipping_rope: await getSvg(StudentIcon.MaleSkippingRope),
    glasses: await getSvg(StudentIcon.MaleGlasses),
    juggler: await getSvg(StudentIcon.MaleJuggler),
  },
  female: {
    close: await getSvg(StudentIcon.FemaleClose),
    open: await getSvg(StudentIcon.FemaleOpen),
    bounce: await getSvg(StudentIcon.FemaleHeadBounce),
    jump: await getSvg(StudentIcon.FemaleJumpSpin),
    snooze: await getSvg(StudentIcon.FemaleSnooze),
    skipping_rope: await getSvg(StudentIcon.FemaleSkippingRope),
    glasses: await getSvg(StudentIcon.FemaleGlasses),
    juggler: await getSvg(StudentIcon.FemaleJuggler),
  },
  other: {
    close: await getSvg(StudentIcon.OtherClose),
    open: await getSvg(StudentIcon.OtherOpen),
    bounce: await getSvg(StudentIcon.OtherHeadBounce),
    jump: await getSvg(StudentIcon.OtherJumpSpin),
    snooze: await getSvg(StudentIcon.OtherSnooze),
    skipping_rope: await getSvg(StudentIcon.OtherSkippingRope),
    glasses: await getSvg(StudentIcon.OtherGlasses),
    juggler: await getSvg(StudentIcon.OtherJuggler),
  },
});

const getClassIcons = async(): Promise<ScatterClassIconType> => ({
  selected: await getSvg(ClassIcon.SelectedClass),
  positive: await getSvg(ClassIcon.PositiveClass),
  negative: await getSvg(ClassIcon.NegativeClass),
});

const getChevronIcons = async(): Promise<{
  chevronUp: Node
  chevronDown: Node
}> => ({
  chevronUp: await getSvg(ChevronUp),
  chevronDown: await getSvg(ChevronDown),
});

const getCollapseIcons = async(): Promise<{
  collapseOn: Node
  collapseOff: Node
}> => ({
  collapseOn: await getSvg(CollapseOn),
  collapseOff: await getSvg(CollapseOff),
});

const getChartIcon = async(): Promise<Node> => await getSvg(ChartColoured);

const getSmileyIcons = async(): Promise<{
  bad: Node,
  good: Node,
  excellent: Node,
}> => ({
  bad: await getSvg(BadSmiley),
  good: await getSvg(GoodSmiley),
  excellent: await getSvg(ExcellentSmiley),
});

export const useIcons = (): IconsType | null => {
  const [ icons, setIcons ] = useState<IconsType | null>(null);

  useEffect(() => {
    const getIcons = async(): Promise<IconsType> => ({
      studentIcons: await getStudentIcons(),
      classesIcons: await getClassIcons(),
      chevronIcons: await getChevronIcons(),
      chartIcon: await getChartIcon(),
      smileyIcons: await getSmileyIcons(),
      collapseIcons: await getCollapseIcons(),
    });

    getIcons().then(setIcons);
  }, [ setIcons ]);

  return icons;
};
